import React from 'react';

// Styles & Assets
import styles from './DialogAddDevice.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useRef, useEffect, useState } from 'react';
import useHttp from '../../../hooks/useHttp';

// Components
import InputSelect from '../../UI/InputSelect/InputSelect';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import Input from '../../UI/Input/Input';

// Interface
import { DeviceManufacturerInterface, DeviceType } from '../../../interfaces/Device';
import { OptionsSelect } from '../../../interfaces/Input';

// Language
import { useTranslation } from 'react-i18next';

interface Props {
    getAllDevices: () => void
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}
const DialogAddDevice: React.FC<Props> = ({ getAllDevices, setShowModal }) => {

    const { t } = useTranslation();

    const { sendRequest: getDeviceTypes } = useHttp<DeviceType[]>();
    const { sendRequest: getManufacturers } = useHttp<DeviceManufacturerInterface[]>();
    const { sendRequest: postDevices } = useHttp();

    const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
    const [deviceManufacturers, setDeviceManufacturers] = useState<DeviceManufacturerInterface[]>([]);

    const [deviceTypeID, setDeviceTypeID] = useState<number | string | null>(null);
    const [manufacturer, setManufacturer] = useState<DeviceManufacturerInterface | null>(null);
    const [modelID, setModelID] = useState<number | string | null>(null);
    const quantity = useRef<number>(0);
    const [errorForm, setErrorForm] = useState<null | string>(null);

    useEffect(() => {
        getDeviceTypes({
            url: getEnvironmentApiUrl() + 'device_type/all',
            method: 'POST',
            body: { params: {}, filter: {} },
        }, (data) => setDeviceTypes(data));
    }, [getDeviceTypes]);

    useEffect(() => {
        getManufacturers({
            url: getEnvironmentApiUrl() + 'device_manufacturer/all',
            method: 'POST',
            body: { params: {}, filter: {} },
        }, (data) => setDeviceManufacturers(data));
    }, [getManufacturers]);

    const handleChangeDeviceType = (option: OptionsSelect) =>
        setDeviceTypeID(option.id);

    const handleChangeManufacturer = (option: OptionsSelect) =>
        setManufacturer(deviceManufacturers.find(manufacturer => manufacturer.id === option.id) || null);

    const handleChangeModel = (option: OptionsSelect) =>
        setModelID(option.id);

    const handleChangeQuantity = (evt: React.ChangeEvent<HTMLInputElement>) =>
        quantity.current = Number(evt.target.value);

    const handleAddDevices = () => {
        const deviceType = deviceTypes.find(deviceType => deviceType.id === deviceTypeID);
        if (deviceType === undefined || manufacturer === null || modelID === null) {
            setErrorForm(t('all_fields_required'));
            return;
        }
        if (quantity.current <= 0) {
            setErrorForm(t('quantity_higher') + ' 0.');
            return;
        }
        postDevices({
            url: getEnvironmentApiUrl() + 'device/multiple',
            method: 'POST',
            body: {
                n_devices: quantity.current,
                data: {
                    device_type_id: deviceType.id,
                    serial_number: null,
                    qr_code: null,
                    device_model_id: modelID,
                    device_type: deviceType,
                }
            },
        }, () => getAllDevices());
        setShowModal(false);
    };

    const deviceTypeName = deviceTypes.find(deviceType => deviceType.id === deviceTypeID)?.name || '';
    const modelName = manufacturer?.models?.find(model => model.id === modelID)?.name || '';

    const getManufacturersOfDeviceType = () => {
        if (deviceTypeID === null) return [];
        return deviceManufacturers.filter(manufacturer => manufacturer.models?.some(m => m.device_type_id === deviceTypeID));
    }

    const modelsWithDeviceType = getManufacturersOfDeviceType()

    return (
        <div className='dialog'>
            <h1 className={`title ${styles.title}`}>{t('add_devices')}</h1>
            <form className={styles.form}>
                <div className={styles.inputs}>
                    <div className={styles.select}>
                        <InputSelect
                            options={deviceTypes.map(devicetype => ({
                                id: devicetype.id || '',
                                name: devicetype.name || ''
                            }))}
                            value={deviceTypeName}
                            label={t('device_type')}
                            onChange={handleChangeDeviceType}
                        />
                    </div>
                    <div className={styles.select}>
                        <InputSelect
                            options={modelsWithDeviceType.map(deviceManufacturer => ({
                                id: deviceManufacturer.id || '',
                                name: deviceManufacturer.name || ''
                            }))}
                            value={manufacturer?.name || ''}
                            label={t('manufacturer')}
                            onChange={handleChangeManufacturer}
                        />
                    </div>
                </div>
                <div className={styles.inputs}>
                    <div className={styles.select}>
                        <InputSelect
                            options={manufacturer?.models?.map(deviceModel => ({
                                id: deviceModel.id || '',
                                name: deviceModel.name || ''
                            })) || []}
                            value={modelName}
                            label={t('model')}
                            onChange={handleChangeModel}
                            disabled={manufacturer === null}
                        />
                    </div>
                    <div className={styles.select}>
                        <Input
                            label={t('quantity') as string}
                            onChange={handleChangeQuantity}
                            type='number'
                        />
                    </div>
                </div>
                {errorForm && <p className={styles.errorMsg}>{errorForm}</p>}
            </form>
            <CancelConfirmButtons
                onCancel={() => setShowModal(false)}
                onConfirm={handleAddDevices}
                confirmName={t('add') as string}
            />
        </div>
    );
};

export default DialogAddDevice;